import { useLoadScript } from "@react-google-maps/api";
import { useCallback, useEffect, useRef } from "react";
import { googlePlaceToPoi, parseGoogleAddressComponents } from "@/components/organisms/MyFavourites/FavouriteLocationsTable/suggestions";
import { GOOGLE_MAPS_LOADER_OPTIONS } from "@/lib/googleMaps";
import { snapToNearestRoad } from "@/lib/googleRoads";
import { PointsOfInterest } from "@/redux/apis/clientPoi/types";
import { useAppSelector } from "@/redux/hooks";
import { defaultCountrySelector } from "@/redux/slices/operator/selectors";

/**
 * Addresses resolved at a time. Google's client-side Places services rate-limit, and a 50-row file
 * can be dozens of distinct addresses — enough to trip them if fired all at once.
 */
const CONCURRENCY = 5;

export type ResolvedAddressMap = Map<string, PointsOfInterest>;

/**
 * Resolves spreadsheet addresses the same way the booking sheet resolves a typed one.
 *
 * **Why not just geocode them server-side:** because bulk booking is N normal bookings, and every
 * step the two flows do differently is a divergence waiting to surface. The booking sheet goes
 * Places Autocomplete → Place Details → snap to nearest road, and the coordinates that come out of
 * that are what its prices are calculated from. Resolving bulk addresses any other way produces
 * different coordinates, so the same trip can price differently depending on how it was booked.
 *
 * So this runs the identical calls with the identical helpers (`googlePlaceToPoi`,
 * `parseGoogleAddressComponents`, `snapToNearestRoad`). The only difference is unavoidable: there's
 * no human to pick from the prediction list, so it takes the top one — which is why the review step
 * shows what was matched.
 *
 * Addresses are **de-duplicated** before resolving. Bulk files repeat themselves relentlessly, so
 * fifty airport transfers for one company is usually two or three lookups, not a hundred.
 */
export const useBulkAddressResolution = () => {
  const country = useAppSelector(defaultCountrySelector);
  // Reuses the single @react-google-maps/api loader (`GOOGLE_MAPS_LOADER_OPTIONS`) — never add a second.
  const { isLoaded } = useLoadScript(GOOGLE_MAPS_LOADER_OPTIONS);

  const servicesRef = useRef<{
    autocomplete: google.maps.places.AutocompleteService;
    places: google.maps.places.PlacesService;
  } | null>(null);

  useEffect(() => {
    if (isLoaded && !servicesRef.current && window.google?.maps?.places) {
      servicesRef.current = {
        autocomplete: new google.maps.places.AutocompleteService(),
        places: new google.maps.places.PlacesService(document.createElement("div")),
      };
    }
  }, [isLoaded]);

  /** Top Autocomplete prediction for a free-text address, or null when Google has no match. */
  const predict = useCallback(
    (address: string) =>
      new Promise<google.maps.places.AutocompletePrediction | null>((resolve) => {
        const service = servicesRef.current?.autocomplete;
        if (!service) return resolve(null);

        service.getPlacePredictions(
          // NO `types` restriction — exactly as useAddressSearch does, and for the reason its comment
          // gives: `types: ["address"]` returns street addresses only and excludes establishments, so
          // "Sydney Opera House" or "MOOII Regent Place" match nothing at all. Spreadsheets are full
          // of place names, so this restriction silently broke most real input.
          { input: address, ...(country ? { componentRestrictions: { country } } : {}) },
          (predictions) => resolve(predictions?.[0] ?? null)
        );
      }),
    [country]
  );

  /** Place Details + road snap for a chosen prediction — the booking sheet's `resolvePlace`. */
  const detail = useCallback(
    (prediction: google.maps.places.AutocompletePrediction) =>
      new Promise<PointsOfInterest | null>((resolve) => {
        const service = servicesRef.current?.places;
        if (!service) return resolve(null);

        service.getDetails({ placeId: prediction.place_id, fields: ["geometry.location", "address_components"] }, async (details) => {
          const location = details?.geometry?.location;
          if (!location) return resolve(null);

          // Snapping matters: a place's geometry is often a building centroid, and the booking sheet
          // prices from the snapped point. Skipping it would make bulk fares drift from manual ones.
          const snapped = await snapToNearestRoad({ lat: location.lat(), lng: location.lng() });
          const parts = parseGoogleAddressComponents(details?.address_components ?? []);

          resolve(
            googlePlaceToPoi(
              {
                description: prediction.description,
                place_id: prediction.place_id,
                types: prediction.types,
                structured_formatting: {
                  main_text: prediction.structured_formatting?.main_text ?? prediction.description,
                  secondary_text: prediction.structured_formatting?.secondary_text ?? "",
                },
              },
              snapped,
              parts
            )
          );
        });
      }),
    []
  );

  /**
   * Resolve many addresses, de-duplicated and rate-limited.
   *
   * Returns a map keyed by the ORIGINAL string, so callers look up each row's address as written. A
   * key is absent when Google had no match — the caller turns that into a row error rather than
   * guessing.
   */
  const resolveAddresses = useCallback(
    async (addresses: string[], onProgress?: (done: number, total: number) => void): Promise<ResolvedAddressMap> => {
      const distinct = [...new Set(addresses.map((address) => address.trim()).filter(Boolean))];
      const resolved: ResolvedAddressMap = new Map();
      let done = 0;

      // A simple worker pool: CONCURRENCY workers pulling from one queue keeps us inside Google's
      // client-side limits without serialising the whole file.
      let cursor = 0;
      const worker = async () => {
        while (cursor < distinct.length) {
          const address = distinct[cursor++];
          const prediction = await predict(address);
          const poi = prediction ? await detail(prediction) : null;
          if (poi) resolved.set(address, poi);

          done += 1;
          onProgress?.(done, distinct.length);
        }
      };

      await Promise.all(Array.from({ length: Math.min(CONCURRENCY, distinct.length) }, worker));

      return resolved;
    },
    [predict, detail]
  );

  return { resolveAddresses, isReady: isLoaded };
};
